import { useEffect, useRef, useState } from 'react';
import { stats, values } from '../../mocks/homeData';

export default function AboutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" ref={sectionRef} className="bg-teal-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <div
                className={`transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <div className="w-10 h-0.5 bg-teal mb-10"></div>
                <div className="grid grid-cols-2 gap-8">
                  {stats.map((stat, index) => (
                    <div
                      key={stat.label}
                      className={`transition-all duration-700 ${
                        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                      }`}
                      style={{ transitionDelay: `${index * 150}ms` }}
                    >
                      <p className="font-heading font-bold text-teal text-3xl md:text-4xl">{stat.number}</p>
                      <p className="font-body text-charcoal text-sm mt-1">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-8">
              <div
                className={`transition-all duration-700 ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
              >
                <span className="font-body font-semibold text-teal text-xs uppercase tracking-widest">Why Work With Us</span>
                <h2 className="font-heading font-bold text-charcoal text-2xl md:text-3xl lg:text-4xl mt-2 mb-4">
                  Honesty and Integrity — In Every Job, Every Time
                </h2>

                <p className="font-body text-charcoal/70 text-sm md:text-base leading-relaxed mb-4">
                  At Aria Property Improvement, we believe that trust isn't just earned — it's built into every wall, every tile, 
                  and every nail we drive. For over 15 years, South Florida homeowners and businesses have counted on us for honest 
                  assessments, transparent pricing, and work that stands the test of time.
                </p>
                <p className="font-body text-charcoal/70 text-sm md:text-base leading-relaxed mb-8">
                  We don't cut corners and we don't inflate costs. What you see in your estimate is what you pay — no surprises, no 
                  hidden fees. Our crew treats your property with the same care and respect they'd give their own home. That's not 
                  just a nice thing to say — it's how we've earned every one of our 500+ completed projects and the 98% satisfaction 
                  rating our clients consistently give us.
                </p>

                <div className="space-y-4">
                  {values.map((value, index) => (
                    <div
                      key={value.id}
                      className={`flex items-start gap-4 p-4 bg-white rounded-lg border-l-3 border-teal transition-all duration-700 ${
                        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                      }`}
                      style={{ transitionDelay: `${(index + 1) * 200}ms` }}
                    >
                      <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 bg-teal/10 rounded-md">
                        <i className={`${value.icon} text-teal text-lg`}></i>
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-charcoal text-base">{value.title}</h3>
                        <p className="font-body text-charcoal/70 text-sm leading-relaxed mt-1">{value.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}